import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { PacmanLoader } from "react-spinners";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function NzSuburbList({
  isAuthenticated,
  user,
  cityscopeOptions,
}) {
  const nzSuburbIdPrefix = "nz-suburb-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);
  const [suburb, setSuburb] = useState("");
  const [scope, setScope] = useState("");

  // --- API Functions ---
  const getData = useCallback(async (filter = {}) => {
    const response = await get({
      apiName: "colin",
      path: "/nzsuburbs",
      options: {
        queryParams: filter,
      },
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localData = await getData();
        setData(localData || []);
      } catch (e) {
        alert(e);
        setUi({ showAlert: true, errorMessage: "Failed to load data." });
      }
      setIsLoading(false);
    })();
  }, [isAuthenticated, getData]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/nzsuburbs/new" : `/nzsuburbs/${record["nz_postcode_id"]}`;
    const method = isNew ? post : put;

    const result = await method({
      apiName: "colin",
      path: path,
      options: { body: { ...record, last_edited_by: user } }
    });
    const response = await result.response;
    return await response.body.json();
  };

  // --- Validation Helper ---
  const validateRecord = (record) => {
    if (String(record.district ?? "").trim().length > 255) {
      return [false, "District cannot exceed 255 characters. The record has not been saved"];
    }
    if (!record.place || record.place.trim() === "") {
      return [false, "Place cannot be blank. The record has not been saved"];
    }
    if (String(record.place).trim().length > 100) {
      return [false, "Place cannot exceed 100 characters. The record has not been saved"];
    }
    if (!record.postcode || String(record.postcode).trim() === "") {
      return [false, "Postcode cannot be blank. The record has not been saved"];
    }
    if (String(record.postcode).trim().length > 4) {
      return [false, "Postcode cannot exceed 4 characters. The record has not been saved"];
    }
    if (!Number.isInteger(Number(String(record.postcode).trim())) || isNaN(Number(String(record.postcode).trim()))) {
      return [false, `Postcode must be a whole number (e.g. 6011). "${String(record.postcode).trim()}" is not valid. The record has not been saved`];
    }
    return [true, ""];
  };

  const getNewRecordValidationMessage = (record, activeFieldId) => {
    const editableFieldIds = ["place", "postcode", "district"];
    if (!editableFieldIds.includes(activeFieldId)) {
      return "";
    }

    const place = String(record.place ?? "").trim();
    const postcode = String(record.postcode ?? "").trim();
    const district = String(record.district ?? "").trim();

    if (activeFieldId === "place" && place === "") {
      return "Place cannot be blank. The record has not been saved";
    }
    if (activeFieldId === "postcode" && postcode === "") {
      return "Postcode cannot be blank. The record has not been saved";
    }

    if (district !== "" && district.length > 255) {
      return "District cannot exceed 255 characters. The record has not been saved";
    }

    if (postcode !== "") {
      if (postcode.length > 4) {
        return "Postcode cannot exceed 4 characters. The record has not been saved";
      }
      if (!/^\d+$/.test(postcode)) {
        return `Postcode must be a whole number (e.g. 6011). "${postcode}" is not valid. The record has not been saved`;
      }
    }

    return "";
  };

  // --- Table Logic ---
  const updateTableData = (rowIndex, columnId, value) => {
    setData(old => old.map((row, index) => {
      if (index === rowIndex) {
        return { ...row, [columnId]: value, modified: true };
      }
      return row;
    }));
  };

  const handleSave = async ({ row, column, value }) => {
    const rowIndex = row.index;
    let normalizedValue = value;
    if (column.id === "postcode") {
      normalizedValue = String(value ?? "").trim().slice(0, 4);
    } else if (column.id === "place") {
      normalizedValue = String(value ?? "").trim().slice(0, 100);
    } else if (column.id === "district") {
      normalizedValue = String(value ?? "").trim().slice(0, 255);
    }
    const recordToSave = { ...row.original, [column.id]: normalizedValue };
    const [isValid, message] = validateRecord(recordToSave);

    setUi(prev => ({ ...prev, showAlert: false, errorMessage: "" }));

    if (recordToSave.new_record) {
      const fieldMessage = getNewRecordValidationMessage(recordToSave, column.id);
      if (fieldMessage) {
        setUi({ showAlert: true, errorMessage: fieldMessage });
        return true;
      }

      if (!isValid) {
        return true;
      }
    }

    if (!isValid) {
      setUi({ showAlert: true, errorMessage: message });
      return true;
    }

    if (recordToSave.isSaving) return;

    setData(old => old.map((r, idx) =>
      idx === rowIndex ? { ...r, isSaving: true, [column.id]: normalizedValue } : r
    ));

    try {
      const response = await saveRecord(recordToSave);

      if (response && response.result === false) {
        setUi({ showAlert: true, errorMessage: response.error_message });
        setData(old => old.map((r, idx) => idx === rowIndex ? { ...r, isSaving: false } : r));
        return false;
      }

      const persistedId = recordToSave.new_record
        ? (response?.id || response?.nz_postcode_id || row.original.nz_postcode_id)
        : row.original.nz_postcode_id;

      setData(old => old.map((r, idx) =>
        idx === rowIndex
          ? {
            ...r,
            [column.id]: normalizedValue,
            nz_postcode_id: persistedId,
            isSaving: false,
            new_record: false,
            modified: false
          }
          : r
      ));
      return true;
    } catch (err) {
      setData(old => old.map((r, idx) => idx === rowIndex ? { ...r, isSaving: false } : r));
      setUi({ showAlert: true, errorMessage: "Network Error: Could not save." });
      return false;
    }
  };

  const addRecord = () => {
    setData(prev => [{
      nz_postcode_id: "",
      cityscope: "",
      place: "",
      district: "",
      postcode: "",
      created_by: user,
      last_edited_by: user,
      new_record: true,
      modified: true,
    }, ...prev]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const filter = {};
      if (suburb) filter.suburb = suburb;
      if (scope) filter.scope = scope;
      const results = await getData(filter);
      setData(results || []);
    } catch (e) {
      console.error("Search Error:", e);
      setUi({ showAlert: true, errorMessage: "Failed to search suburbs." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "suburb") setSuburb(value);
    else if (name === "scope") setScope(value);
  };

  const handleCityscopeChange = (selectedOptions, { row, column }) => {
    const newValue = selectedOptions.map(option => option.value).join(';');
    updateData(row.index, column.id, newValue);
  };

  const handleCityscopeBlur = ({ row, column }) => {
    handleSave({ row: row, column: column });
  };

  const updateData = (rowIndex, columnId, value) => {
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {

          let finalValue = value;

          if (columnId === 'postcode') {
            finalValue = String(value).trim();
          } else {
            if (typeof old[rowIndex][columnId] === 'number') {
              finalValue = Number(value);
            }
          }
          console.log("UPDATE")
          return {
            ...old[rowIndex],
            [columnId]: finalValue,
            modified: true,
          };
        }
        return row;
      })
    );
  };

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("nz_postcode_id", {
      id: "nz_postcode_id",
      header: "ID",
      cell: (info) => {
        const { isSaving, new_record } = info.row.original;
        if (isSaving) return "Saving...";
        if (new_record) return <span className="badge badge-info">New</span>;
        return info.getValue() || '';
      },
      size: 100,
    }),
    columnHelper.accessor("cityscope", {
      id: "cityscope",
      header: "Cityscope",
      cell: EditableCell,
      size: 200,
    }),
    columnHelper.accessor("place", {
      id: "place",
      header: "Place",
      cell: EditableCell,
      meta: { maxLength: 100 },
      size: 100,
    }),
    columnHelper.accessor("district", {
      id: "district",
      header: "District",
      cell: EditableCell,
      meta: { maxLength: 255 },
      size: 100,
    }),
    columnHelper.accessor("postcode", {
      id: "postcode",
      header: "Postcode",
      cell: EditableCell,
      meta: { maxLength: 4 },
      size: 100,
    }),
    columnHelper.accessor("created_by", {
      id: "created_by",
      header: "Created By",
      cell: (info) => info.getValue(),
      size: 100,
    }),
    columnHelper.accessor("last_edited_by", {
      id: "last_edited_by",
      header: "Last Edited By",
      cell: (info) => info.getValue(),
      size: 300,
    }),
  ], []);

  const createId = useMemo(
    () => createElementIdFactory(nzSuburbIdPrefix),
    [nzSuburbIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Form id={createId("search", "form")} onSubmit={handleSubmit} className="mb-3">
        <Row>
          <Col lg={2}>
            <Form.Control id={createId("suburb", "input")} name="suburb" value={suburb} onChange={handleChange} placeholder="Suburb/Place" />
          </Col>
          <Col lg={2}>
            <Form.Control id={createId("scope", "input")} name="scope" value={scope} onChange={handleChange} placeholder="Cityscope" />
          </Col>
          <Col lg={2}>
            <Button id={createId("search", "button")} type="submit">Search</Button>
          </Col>
        </Row>
      </Form>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="nz-suburb-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.nzSuburb}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableData,
                  handleSave: handleSave,
                  shouldForceSaveOnBlur: ({ row, column, value }) => (
                    !!row?.original?.new_record
                    && ["place", "postcode"].includes(column?.id)
                    && String(value ?? "").trim() === ""
                  ),
                  handleCityscopeChange: handleCityscopeChange,
                  handleCityscopeBlur: handleCityscopeBlur,
                  cityscopeOptions: cityscopeOptions,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default NzSuburbList;